import { DestroyRef, inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BoelsWindow, PageViewConsentEvent } from '@boels/models';
import { filter, map, Observable, of, ReplaySubject, skip, take } from 'rxjs';
import { WindowRef } from './window/window-ref';
import { GtmService } from './gtm.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  #winRef: WindowRef = inject(WindowRef);
  #router: Router = inject(Router);
  #gtmService: GtmService = inject(GtmService);
  #detroyRef: DestroyRef = inject(DestroyRef);
  #document = inject<Document>(DOCUMENT);

  private consentGivenSubject: ReplaySubject<boolean> =
    new ReplaySubject<boolean>(1);

  initConsentEventManually(): void {
    if (!this.#winRef.isBrowser()) return;

    const cookieBot = (this.#winRef.nativeWindow as BoelsWindow).CookieConsent;
    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        skip(1),
        takeUntilDestroyed(this.#detroyRef),
      )
      .subscribe(() => {
        if (!cookieBot?.hasResponse) return;
        const cookieBotEvent: PageViewConsentEvent = {
          event: 'cookie_consent_update',
        };
        this.#gtmService.pushTag(cookieBotEvent);
      });
  }

  initConsentEventHandler(): void {
    if (!this.#winRef.isBrowser()) return;

    this.#winRef.nativeWindow?.addEventListener('CookiebotOnAccept', () => {
      this.consentGivenSubject.next(true);
    });
    this.#winRef.nativeWindow?.addEventListener('CookiebotOnDecline', () => {
      this.consentGivenSubject.next(false);
    });
  }

  hasConsentFor$(
    consentType: 'preferences' | 'statistics' | 'marketing' | 'necessary',
  ): Observable<boolean> {
    if (!this.#winRef.isBrowser()) return of(true);

    return this.consentGivenSubject.asObservable().pipe(
      map((consented: boolean) => {
        if (!consented) return consented;
        const cookieBot = (this.#winRef.nativeWindow as BoelsWindow)
          .CookieConsent;
        return cookieBot?.consent[consentType] ?? false;
      }),
    );
  }

  setCookieConsentForLocalhost(): void {
    this.#document.cookie = 'CookieConsent=true; domain=localhost; path=/';
  }

  injectJsWithConsent(
    id: string,
    src: string,
    consentType: 'preferences' | 'statistics' | 'marketing' | 'necessary',
  ): void {
    const script = this.createScriptElement(this.#document, id, src);

    this.hasConsentFor$(consentType)
      .pipe(take(1)) // Automatically complete the subscription after the first emission
      .subscribe((hasConsent) => {
        if (hasConsent) this.#document.head.appendChild(script);
      });
  }

  private createScriptElement(
    document: Document,
    id: string,
    src: string,
  ): HTMLScriptElement {
    const script = document.createElement('script');
    script.id = '';
    script.type = 'text/javascript';
    script.src = src;
    return script;
  }
}
